import { useState, useEffect } from "react";
import { useAuth } from "@/hooks/useAuth";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { CalendarDays, Clock, MapPin, TrendingUp, Swords, Gift, Trophy, Dumbbell } from "lucide-react";
import OpenMatchIcon from "@/components/icons/OpenMatchIcon";
import SportIcon from "@/components/SportIcon";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { format } from "date-fns";
import { sv } from "date-fns/locale";
import { Skeleton } from "@/components/ui/skeleton";
import NewsFeed from "@/components/social/NewsFeed";
import ActiveMatchCard from "@/components/tournaments/ActiveMatchCard";
import LevelBadge from "@/components/level/LevelBadge";
import CommunityFeed from "@/components/community/CommunityFeed";
import { useOrganization } from "@/hooks/useOrganization";
import { useUserRole } from "@/hooks/useUserRole";
import SelfAssessmentModal from "@/components/level/SelfAssessmentModal";
import { useUpcomingActivities, type UpcomingActivity } from "@/hooks/useUpcomingActivities";
import { useTranslation } from "react-i18next";

const activityIcons: Record<string, React.ElementType> = {
  racket: CalendarDays,
  trophy: Trophy,
  swords: Swords,
  dumbbell: Dumbbell,
};

const activityColors: Record<string, string> = {
  racket: "text-primary bg-primary/10 border-l-primary",
  trophy: "text-amber-600 bg-amber-500/10 border-l-amber-500",
  swords: "text-accent-foreground bg-accent/10 border-l-accent",
  dumbbell: "text-teal-600 bg-teal-500/10 border-l-teal-500",
};

export default function Index() {
  const { t } = useTranslation('common');
  const { user, profile } = useAuth();
  const { organization } = useOrganization();
  const { isAdmin } = useUserRole();
  const [showAssessment, setShowAssessment] = useState(false);

  useEffect(() => {
    if (profile && (profile as any).level_assessed_at == null) {
      setShowAssessment(true);
    }
  }, [profile]);

  const { data: activities = [], isLoading } = useUpcomingActivities();

  const { data: playerStats } = useQuery({
    queryKey: ["player-stats-home", user?.id],
    queryFn: async () => {
      const { data } = await supabase
        .from("player_stats_view")
        .select("total_matches, wins, win_rate")
        .eq("player_id", user!.id)
        .maybeSingle();
      return data;
    },
    enabled: !!user,
  });


  // Referral CTA
  const { data: referralCTA } = useQuery({
    queryKey: ["referral-cta", user?.id, organization?.id],
    queryFn: async () => {
      const { data: flags } = await supabase
        .from("feature_flags")
        .select("referral_program_enabled")
        .eq("organization_id", organization!.id)
        .maybeSingle();
      if (!flags?.referral_program_enabled) return null;
      const { data: config } = await supabase
        .from("referral_config")
        .select("enabled, first_referral_bonus")
        .eq("organization_id", organization!.id)
        .maybeSingle();
      if (!config?.enabled) return null;
      const { data: existing } = await supabase
        .from("referral_codes")
        .select("id")
        .eq("user_id", user!.id)
        .maybeSingle();
      if (existing) return null;
      return { bonus: config.first_referral_bonus };
    },
    enabled: !!user && !!organization?.id,
  });

  const firstName = profile?.full_name?.split(" ")[0] || t('player');

  return (
    <div className="space-y-6 pb-20 lg:max-w-full lg:mx-auto lg:px-6">
      {/* Active tournament match */}
      <ActiveMatchCard />

      {/* Self-assessment modal */}
      {user && (
        <SelfAssessmentModal
          open={showAssessment}
          onOpenChange={setShowAssessment}
          userId={user.id}
          organizationId={organization?.id}
          onComplete={() => setShowAssessment(false)}
        />
      )}

      {/* Header */}
      <div className="gradient-hero rounded-2xl p-5 -mx-1 space-y-3">
        <div className="lg:flex lg:items-center lg:justify-between">
          <div>
            <h1 className="text-2xl font-bold">{t('greeting', { name: firstName })}</h1>
            <p className="text-muted-foreground">{(organization?.settings as any)?.home_subtitle || t('readyForMatch')}</p>
          </div>
          <div className="flex items-center gap-3 mt-2 lg:mt-0">
            <LevelBadge level={profile?.level ?? 1} size="md" showLabel />
            {playerStats && (
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <TrendingUp className="h-3.5 w-3.5" />
                <span>{t('matchesAndWinRate', { matches: playerStats.total_matches ?? 0, winRate: playerStats.win_rate ?? 0 })}</span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Desktop: 2-column grid. Mobile: single column */}
      <div className="lg:grid lg:grid-cols-3 lg:gap-8">
        {/* Left column: 2/3 — main content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Quick actions — mobile only (shown in sidebar on desktop) */}
          <div className="grid grid-cols-3 gap-3 lg:hidden">
            <Link to="/book">
              <Card className="hover:bg-muted/30 transition-colors h-full">
                <CardContent className="flex flex-col items-center gap-2 p-4 text-center">
                  <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary/10">
                    <CalendarDays className="h-5 w-5 text-primary" />
                  </div>
                  <span className="text-xs font-medium">{t('booking:title')}</span>
                </CardContent>
              </Card>
            </Link>
            <Link to="/play">
              <Card className="hover:bg-muted/30 transition-colors h-full">
                <CardContent className="flex flex-col items-center gap-2 p-4 text-center">
                  <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary/10">
                    <OpenMatchIcon className="h-5 w-5 text-primary/70" />
                  </div>
                  <span className="text-xs font-medium">{t('findMatch')}</span>
                </CardContent>
              </Card>
            </Link>
            <Link to="/tournaments">
              <Card className="hover:bg-muted/30 transition-colors h-full">
                <CardContent className="flex flex-col items-center gap-2 p-4 text-center">
                  <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-secondary/50">
                    <SportIcon sport={(organization?.settings as any)?.sport_type} size="sm" className="text-secondary-foreground" />
                  </div>
                  <span className="text-xs font-medium">{t('activities:tournaments')}</span>
                </CardContent>
              </Card>
            </Link>
          </div>

          {/* Referral CTA */}
          {referralCTA && (
            <Link to="/profile?tab=referral">
              <Card className="border-primary/20 bg-gradient-to-r from-primary/5 to-accent/5 hover:from-primary/10 hover:to-accent/10 transition-colors">
                <CardContent className="p-4 flex items-center gap-3">
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-primary/10">
                    <Gift className="h-5 w-5 text-primary" />
                  </div>
                  <div className="flex-1">
                    <p className="text-sm font-medium">{t('referralCTA', { bonus: referralCTA.bonus })}</p>
                    <p className="text-xs text-muted-foreground">{t('referralCTADesc')}</p>
                  </div>
                  <span className="text-xs text-primary font-medium">{t('getStartedArrow')}</span>
                </CardContent>
              </Card>
            </Link>
          )}

          {/* Unified upcoming activities */}
          <section className="space-y-3">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold">{t('yourUpcomingActivities')}</h2>
              <Button variant="link" size="sm" asChild className="text-xs">
                <Link to="/bookings">{t('all')} →</Link>
              </Button>
            </div>

            {isLoading ? (
              <div className="space-y-3">
                {[1, 2].map((i) => <Skeleton key={i} className="h-20 w-full rounded-lg" />)}
              </div>
            ) : activities.length > 0 ? (
              activities.map((activity: UpcomingActivity) => {
                const IconComponent = activityIcons[activity.icon] || CalendarDays;
                const colorClass = activityColors[activity.icon] || activityColors.racket;
                const [iconColor, iconBg, borderColor] = colorClass.split(" ");

                return (
                  <Link key={activity.id} to={activity.linkTo}>
                    <Card className={`border-l-[3px] ${borderColor} hover:bg-muted/30 transition-colors`}>
                      <CardContent className="flex items-center gap-4 p-4">
                        <div className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-lg ${iconBg}`}>
                          <IconComponent className={`h-5 w-5 ${iconColor}`} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <p className="font-medium text-sm">
                              {format(activity.startTime, "EEEE d MMM, HH:mm", { locale: sv })}
                              {activity.endTime && `–${format(activity.endTime, "HH:mm")}`}
                            </p>
                            {activity.badge && (
                              <Badge variant="secondary" className="text-[10px] py-0">
                                {activity.badge}
                              </Badge>
                            )}
                          </div>
                          <p className="text-sm text-muted-foreground truncate">{activity.title}</p>
                          {activity.location && (
                            <div className="flex items-center gap-1 text-xs text-muted-foreground">
                              <MapPin className="h-3 w-3" />
                              <span>{activity.location}</span>
                            </div>
                          )}
                        </div>
                        {activity.pinCode && (
                          <span className="text-xs font-mono bg-muted px-2 py-1 rounded shrink-0">
                            PIN: {activity.pinCode}
                          </span>
                        )}
                      </CardContent>
                    </Card>
                  </Link>
                );
              })
            ) : (
              <Card>
                <CardContent className="p-6 text-center space-y-3">
                  <p className="text-muted-foreground">{t('nothingPlanned')}</p>
                  <p className="text-sm text-muted-foreground">
                    {t('bookOrFindMatch')}
                  </p>
                  <div className="flex justify-center gap-2">
                    <Button variant="outline" size="sm" asChild>
                      <Link to="/book">{t('booking:title')} →</Link>
                    </Button>
                    <Button variant="outline" size="sm" asChild>
                      <Link to="/play">{t('findMatch')} →</Link>
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}
          </section>

          {/* News feed */}
          <NewsFeed />
        </div>

        {/* Right column: 1/3 — sidebar */}
        <div className="space-y-6 mt-6 lg:mt-0">
          {/* Quick actions — desktop only */}
          <div className="hidden lg:block space-y-2">
            <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">{t('quickActions')}</h3>
            <div className="space-y-2">
              <Link to="/book">
                <Card className="hover:bg-muted/30 transition-colors">
                  <CardContent className="flex items-center gap-3 p-3">
                    <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary/10">
                      <CalendarDays className="h-4 w-4 text-primary" />
                    </div>
                    <span className="text-sm font-medium">{t('booking:title')}</span>
                  </CardContent>
                </Card>
              </Link>
              <Link to="/play">
                <Card className="hover:bg-muted/30 transition-colors">
                  <CardContent className="flex items-center gap-3 p-3">
                    <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary/10">
                      <OpenMatchIcon className="h-4 w-4 text-primary/70" />
                    </div>
                    <span className="text-sm font-medium">{t('findMatch')}</span>
                  </CardContent>
                </Card>
              </Link>
              <Link to="/tournaments">
                <Card className="hover:bg-muted/30 transition-colors">
                  <CardContent className="flex items-center gap-3 p-3">
                    <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-secondary/50">
                      <SportIcon sport={(organization?.settings as any)?.sport_type} size="sm" className="text-secondary-foreground" />
                    </div>
                    <span className="text-sm font-medium">{t('activities:tournaments')}</span>
                  </CardContent>
                </Card>
              </Link>
            </div>
          </div>

          {/* Community */}
          <section className="space-y-3">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold lg:text-sm lg:font-semibold lg:text-muted-foreground lg:uppercase lg:tracking-wide">{t('clubCommunity')}</h2>
              <Button variant="link" size="sm" asChild className="text-xs">
                <Link to="/community">{t('showAll')} →</Link>
              </Button>
            </div>
            <CommunityFeed limit={3} showComposer={false} showFilters={false} />
          </section>
        </div>
      </div>
    </div>
  );
}
